import { TILE } from './constants';
import { createRng, nextInt } from './rng';
import { UNIT_BY_NAME, unitDef } from '../content/units';
import { BUILDING_BY_NAME, buildingDef } from '../content/buildings';
import { PRODUCER_IDS } from '../content/production';
import { startRotation } from '../content/maps';
import type { RotatableStarts, StartSlot } from '../content/maps';
import { chooseStartSlots } from './starts';
import { getMap } from '../content/maps';
import type { MapId } from '../content/maps';
import { isWalkable } from './path';
import type { Grid } from './path';
import { Phase, UnitMode } from './types';
import type { Building, GameState, MatchConfig, PlayerState, Unit } from './types';

/*
 * בניית מצב פתיחה.
 *
 * הפונקציה טהורה ותלויה **רק** ב-MatchConfig: אותו seed, אותה מפה ואותן משבצות
 * שחקנים מייצרים תמיד בדיוק את אותו מצב פתיחה. זה מה שהופך משחק שלם לניתן
 * לשחזור מ-{seed, mapId, slots} + רשימת פקודות — הבסיס ל-replay ולמולטיפלייר.
 */

/** הרכב הפתיחה של כל שחקן ב-M2. יהפוך לדאטה של תוכן כשתתווסף בנייה. */
const STARTING_SQUAD: readonly (keyof typeof UNIT_BY_NAME)[] = [
    'grizzly',
    'grizzly',
    'gi',
    'gi',
    'gi',
    'harvester',
    'harvester',
];

/** מבני הפתיחה של כל שחקן, כהיסט מנקודת ההתחלה שלו. */
const STARTING_BASE: readonly { name: keyof typeof BUILDING_BY_NAME; dx: number; dy: number }[] = [
    { name: 'yard', dx: -1, dy: -1 },
    { name: 'refinery', dx: 3, dy: -1 },
    { name: 'power', dx: -1, dy: 3 },
];

/**
 * האם לסובב את פריסת הפתיחה של השחקן ב-180 מעלות.
 *
 * **זו לא קוסמטיקה — זה תיקון הוגנות.** ההיסטים למעלה מוחלטים: המפעל תמיד ימינה
 * מהמפקדה. במפה סימטרית ב-180 מעלות זה אומר ששחקן אחד מקבל את המפעל שלו לכיוון
 * האויב ושחקן שני לכיוון ההפוך — כלומר האחד מאבד את הכלכלה שלו ראשון. `simBatch`
 * מדד את זה: במפת דו-קרב עמדה 2 ניצחה כמעט תמיד, עם פי ארבעה מבנים בסוף.
 *
 * הכלל: מי שנקודת הפתיחה שלו מעבר למרכז המפה מקבל את אותה פריסה מסובבת ב-180 —
 * בדיוק הסיבוב שהמפה עצמה סימטרית תחתיו, ולכן הפתיחה נעשית סימטרית לחלוטין.
 */
/**
 * כמה רבעי סיבוב מפרידים בין משבצת הפתיחה של שחקן לזו של הראשון.
 *
 * **נגזר ממסלול הסיבוב ולא מאזור במפה.** חלוקה לרבעים לפי צירים או אלכסונים
 * נשברת ברגע שמשבצת יושבת בדיוק על קו החלוקה — ב-`goldbasin` שתי נקודות
 * הפתיחה על ציר אופקי, וכלל רבעים היה מחזיר סיבוב של 90 מעלות על מפה שסימטרית
 * רק ב-180. כאן זה תמיד ה-k שמעביר בפועל את משבצת 0 אל משבצת השחקן.
 *
 * מלכודת 44 היא בדיוק המקרה הזה בגרסה של שני שחקנים.
 */
function quarterTurns(starts: RotatableStarts, playerIndex: number): number {
    const turns = startRotation(starts, playerIndex);
    return turns < 0 ? 0 : turns;
}

/** מסובב היסט ברבעי סיבוב עם כיוון השעון. ‏y כלפי מטה, ולכן `(dx,dy) → (-dy,dx)`. */
function rotateOffset(dx: number, dy: number, turns: number): { dx: number; dy: number } {
    let x = dx;
    let y = dy;
    for (let i = 0; i < (turns % 4 + 4) % 4; i++) {
        const nx = -y;
        y = x;
        x = nx;
    }
    return { dx: x, dy: y };
}

export function createGrid(mapId: MapId): Grid {
    const map = getMap(mapId);
    return { w: map.w, h: map.h, terrain: map.terrain };
}

export function createMatch(config: MatchConfig): GameState {
    const map = getMap(config.mapId as MapId);
    const grid: Grid = { w: map.w, h: map.h, terrain: map.terrain };
    const rng = createRng(config.seed);

    /*
     * **נקודות הפתיחה מוגרלות לכל משחק** (`sim/starts.ts`), ומכאן ואילך הן —
     * ולא `map.def.startSlots` — מקור האמת. הסיבובים נמדדים מולן מאותה סיבה:
     * מספר שנגזר מהמפה הכתובה היה מתאר פריסה שאינה זו שעל המסך.
     */
    const startSlots: readonly StartSlot[] =
        config.startSlots ?? chooseStartSlots(map, config.seed);
    const starts: RotatableStarts = { w: map.w, h: map.h, startSlots };

    const players: PlayerState[] = config.slots.map((slot, index) => ({
        index,
        kind: slot.kind,
        credits: 5000,
        defeated: false,
        difficulty: slot.difficulty ?? 0,
        // הכול מוצל בפתיחה. הטיק הראשון של `step` חושף את הבסיס עצמו.
        explored: new Uint8Array(map.w * map.h),
        unitsBuilt: 0,
        unitsLost: 0,
        queues: PRODUCER_IDS.map(() => []),
    }));

    const units: Unit[] = [];
    const buildings: Building[] = [];
    let nextEntityId = 1;

    // מבני הפתיחה נוצרים לפני היחידות, כדי שמזהיהם יהיו יציבים בין הרצות.
    config.slots.forEach((_slot, playerIndex) => {
        const start = startSlots[playerIndex];
        if (!start) return;
        const turns = quarterTurns(starts, playerIndex);
        for (const spec of STARTING_BASE) {
            const type = BUILDING_BY_NAME[spec.name];
            /*
             * מבנה תופס w×h **ימינה ולמטה מהפינה**, ולכן סיבוב ההיסט לבדו היה
             * מזיז את המבנה אבל לא את הגוף שלו. מסובבים את **מרכז** המבנה
             * וגוזרים ממנו את הפינה — כך הפריסה היא באמת תמונת ראי, לא רק
             * נקודות שהוזזו. כל מבני הפתיחה ריבועיים בדיוק כדי שזה יֵצא שלם.
             */
            const def = buildingDef(type);
            const centerX = spec.dx + (def.w - 1) / 2;
            const centerY = spec.dy + (def.h - 1) / 2;
            const rotated = rotateOffset(centerX, centerY, turns);
            const dx = Math.round(rotated.dx - (def.w - 1) / 2);
            const dy = Math.round(rotated.dy - (def.h - 1) / 2);
            buildings.push({
                id: nextEntityId++,
                owner: playerIndex,
                type,
                tileX: start.x + dx,
                tileY: start.y + dy,
                hp: buildingDef(type).hp,
                rallyTile: -1,
            repairing: 0,
                targetId: -1,
                cooldown: 0,
                burstLeft: 0,
            });
        }
    });

    config.slots.forEach((_slot, playerIndex) => {
        const start = startSlots[playerIndex];
        if (!start) return;

        // הכוח מפוזר סביב הבסיס; הכיוון נגזר **מאותו סיבוב** שקיבלו המבנים.
        const turns = quarterTurns(starts, playerIndex);
        for (const name of STARTING_SQUAD) {
            const type = UNIT_BY_NAME[name];
            const tile = findFreeTileNear(grid, start.x, start.y, units, buildings, map.w, turns);
            units.push({
                id: nextEntityId++,
                owner: playerIndex,
                type,
                x: (tile % map.w) * TILE + TILE / 2,
                y: Math.floor(tile / map.w) * TILE + TILE / 2,
                hp: unitDef(type).hp,
                mode: UnitMode.Idle,
                goalTile: -1,
                path: [],
                // העוגן מתחיל במקום הפתיחה; הרצועה נמדדת ממנו עד שתגיע פקודה.
                homeX: (tile % map.w) * TILE + TILE / 2,
                homeY: Math.floor(tile / map.w) * TILE + TILE / 2,
                targetId: -1,
                cooldown: 0,
                burstLeft: 0,
                cargo: 0,
            });
        }
    });

    // מגרילים פעם אחת כדי שזרם העולם לא יתחיל במצבו הבתולי — כך שינוי בסדר
    // האתחול ייראה מיד בגיבוב במקום להסתתר עד שמישהו יגריל בפעם הראשונה.
    nextInt(rng, 1024);

    return {
        tick: 0,
        rng,
        seed: config.seed >>> 0,
        nextEntityId,
        mapId: config.mapId,
        startSlots,
        ore: Uint16Array.from(map.ore),
        units,
        buildings,
        projectiles: [],
        players,
        phase: Phase.Playing,
        winner: -1,
        pathQueue: [],
        events: [],
    };
}

/**
 * מוצא אריח פנוי קרוב לנקודת הפתיחה. ספירלה קבועה — היחידות מסודרות תמיד באותו
 * סדר, ולכן מצב הפתיחה זהה בין הרצות.
 */
function findFreeTileNear(
    grid: Grid,
    centerX: number,
    centerY: number,
    placed: readonly Unit[],
    buildings: readonly Building[],
    mapW: number,
    /**
     * רבעי סיבוב — **אותו מספר בדיוק שמסובב את המבנים**.
     *
     * קודם היה כאן דגל של ±1, ולכן ארבע אפשרויות סיבוב נדחסו לשתיים: עמדות
     * שהבסיס שלהן מסובב ב-90 מעלות זו מזו קיבלו פריסת יחידות **זהה**. התוצאה
     * נמדדה — מרחק קוצר→מפעל של 5.4 אריחים בשתי עמדות מול 2.4 בשתיים האחרות,
     * ומזה פער של 29% בייצור לאורך המשחק. ‏`sim/fairness.test.ts` אוכף.
     */
    turns: number,
): number {
    const taken = new Set<number>();

    /*
     * לא רק האריח של היחידה — גם שמונת שכניו. בלי זה כוח הפתיחה נולד דחוס
     * אריח-באריח ונראה כמו גוש אחד, וקשה להקיש על יחידה בודדת. אותו מרווח
     * שהתצורה נותנת בתנועה (ראו SPACING ב-formation.ts), כבר מהשנייה הראשונה.
     */
    for (const unit of placed) {
        const ux = Math.floor(unit.x / TILE);
        const uy = Math.floor(unit.y / TILE);
        for (let dy = -1; dy <= 1; dy++) {
            for (let dx = -1; dx <= 1; dx++) taken.add((uy + dy) * mapW + (ux + dx));
        }
    }

    /*
     * גם שטח המבנים תפוס. בלי זה יחידות הפתיחה נולדות בתוך המפקדה ומצוירות עליה —
     * הבסיס נראה שבור כבר בשנייה הראשונה של המשחק.
     */
    for (const building of buildings) {
        const def = buildingDef(building.type);
        for (let dy = 0; dy < def.h; dy++) {
            for (let dx = 0; dx < def.w; dx++) {
                taken.add((building.tileY + dy) * mapW + (building.tileX + dx));
            }
        }
    }

    for (let ring = 0; ring < 12; ring++) {
        for (let dy = -ring; dy <= ring; dy++) {
            for (let dx = -ring; dx <= ring; dx++) {
                if (Math.max(Math.abs(dx), Math.abs(dy)) !== ring) continue;
                // אותה פונקציית סיבוב שמסובבת את המבנים — ולכן אותה תוצאה בדיוק.
                const rotated = rotateOffset(dx, dy, turns);
                const x = centerX + rotated.dx;
                const y = centerY + rotated.dy;
                if (!isWalkable(grid, x, y)) continue;
                const index = y * mapW + x;
                if (taken.has(index)) continue;
                return index;
            }
        }
    }
    return centerY * mapW + centerX;
}
